import { API, Auth } from "aws-amplify";
import {
  GetPodSession,
  GetPodSessionClaimState,
  GetOrganizationDetails,
  getCraneGuageSession,
  listCraneGuageSessions,
  GetPodNotesFormat,
  getCBRegistrations,
  getUser,
  getGoogleRoomDetails,
  getServerTime,
  PodAgentAlertsByOrganizationId,
  ListPodExamCodesByClientId,
  listUsers,
  syncPodMasterSheet,
} from "../../graphql/queries";
import {
  ClaimPodSession,
  createPodSessionHistory,
  createUser,
  notifyPodSessionClaim,
  submitPodProcessor,
  UpdateCraneGuageSession,
  UpdatePodSession,
  updateUser,
} from "../../graphql/mutations";
import { onUpdatePodSession } from "../../graphql/subscriptions";
import { fetchAllCraneGuageSessionPages } from "./craneSessions";
import { isUnclaimedProctor } from "./sessionUtils";

const PAGE_LIMIT = 1000;
const LOG_PREFIX = "[PodV2 API]";
const CANONICAL_UNCLAIMED_PROCTOR_VALUES = ["UNCLAIMED", "null", ""];
const POD_AGENT_ALERT_ORGANIZATION_GROUPS = [
  ["kennesaw-state-university", "kennesaw-state-university-scheduled"],
  [
    "louisiana-state-university-of-alexandria-lsua",
    "louisiana-state-university-of-alexandria-lsua-scheduled",
  ],
];

function log(label, ...args) {
  console.log(`${LOG_PREFIX} ${label}`, ...args);
}

function getPodAgentAlertOrganizationIds(organizationId) {
  const group = POD_AGENT_ALERT_ORGANIZATION_GROUPS.find((ids) =>
    ids.includes(organizationId),
  );

  return group ?? [organizationId];
}

function getNonEmptyString(value) {
  const str = String(value ?? "").trim();
  return str || null;
}

export class PodSessionAlreadyClaimedError extends Error {
  constructor(session) {
    super("Pod session is already claimed");
    this.name = "PodSessionAlreadyClaimedError";
    this.session = session;
  }
}

function isConditionalCheckFailed(error) {
  const graphqlError = error?.errors?.[0] ?? error;
  const errorType = graphqlError?.errorType ?? error?.errorType ?? "";
  const errorInfo = graphqlError?.errorInfo ?? error?.errorInfo;
  const message = graphqlError?.message ?? error?.message ?? "";

  return (
    String(errorType).includes("ConditionalCheckFailedException") ||
    String(errorInfo?.code ?? errorInfo ?? "").includes(
      "ConditionalCheckFailedException",
    ) ||
    String(message).includes("ConditionalCheckFailedException")
  );
}

function getConditionalCheckFailedData(error) {
  return error?.errors?.[0]?.data ?? null;
}

function attachHistoryError(session, historyError) {
  if (!session || !historyError) return session;
  Object.defineProperty(session, "historyError", {
    value: historyError,
    enumerable: false,
    configurable: true,
  });
  return session;
}

async function writePodSessionHistory(historyInput, authMode) {
  try {
    const { data: historyData } = await API.graphql({
      query: createPodSessionHistory,
      variables: { input: historyInput },
      authMode,
    });
    log(
      "createPodSessionHistory → response",
      historyData.createPodSessionHistory,
    );
    return null;
  } catch (error) {
    console.error("createPodSessionHistory failed:", error);
    return error;
  }
}

async function fetchCurrentActor(authMode) {
  try {
    const cognitoUser = await Auth.currentUserInfo();
    const cognitoUserId = getNonEmptyString(cognitoUser?.id);
    const cognitoEmail = getNonEmptyString(cognitoUser?.attributes?.email);

    if (!cognitoUserId) {
      return cognitoEmail || "unknown";
    }

    const { data } = await API.graphql({
      query: getUser,
      variables: { id: cognitoUserId },
      authMode,
    });

    const user = data.getUser;
    return (
      getNonEmptyString(user?.username) ||
      getNonEmptyString(user?.email) ||
      cognitoEmail ||
      "unknown"
    );
  } catch (error) {
    console.error("Unable to resolve PodSessionHistory actor:", error);
    return "unknown";
  }
}

async function fetchAllPages(queryName, query, connectionKey, variables) {
  log(`${queryName} → start`, variables);

  const allItems = [];
  let nextToken = null;
  let page = 0;

  do {
    page += 1;
    const requestVariables = {
      ...variables,
      limit: PAGE_LIMIT,
      nextToken: nextToken || undefined,
    };

    log(`${queryName} → page ${page}`, requestVariables);

    const { data } = await API.graphql({
      query,
      variables: requestVariables,
    });

    const result = data[connectionKey];
    const items = result?.items ?? [];

    log(`${queryName} → page ${page} response`, {
      itemCount: items.length,
      nextToken: result?.nextToken ?? null,
    });

    allItems.push(...items);
    nextToken = result?.nextToken ?? null;
  } while (nextToken);

  log(`${queryName} → complete`, { totalItems: allItems.length });

  return allItems;
}

export async function fetchPodSession(id) {
  log("fetchPodSession → start", { id });

  const { data } = await API.graphql({
    query: GetPodSession,
    variables: { id },
  });

  const session = data.getPodSession ?? null;
  log("fetchPodSession → response", session);

  return session;
}

const POD_SESSION_HISTORY_PAGE_LIMIT = 50;
const POD_SESSION_HISTORY_MAX_PAGES = 20;

const PodSessionHistoryById = /* GraphQL */ `
  query PodSessionHistoryById(
    $id: ID
    $sortDirection: ModelSortDirection
    $limit: Int
    $nextToken: String
  ) {
    listPodSessionHistories(
      id: $id
      sortDirection: $sortDirection
      limit: $limit
      nextToken: $nextToken
    ) {
      nextToken
      items {
        id
        timestamp
        actor
        input
      }
    }
  }
`;

export async function fetchPodSessionHistory(id) {
  log("fetchPodSessionHistory → start", { id });

  if (!id) return { items: [], truncated: false };

  const items = [];
  let nextToken = null;
  let page = 0;

  do {
    page += 1;
    const requestVariables = {
      id,
      sortDirection: "DESC",
      limit: POD_SESSION_HISTORY_PAGE_LIMIT,
      nextToken: nextToken || undefined,
    };

    log(`fetchPodSessionHistory → page ${page}`, requestVariables);

    const { data } = await API.graphql({
      query: PodSessionHistoryById,
      variables: requestVariables,
    });

    const connection = data?.listPodSessionHistories;
    const pageItems = Array.isArray(connection?.items) ? connection.items : [];

    items.push(...pageItems.filter(Boolean));
    nextToken = connection?.nextToken ?? null;
  } while (nextToken && page < POD_SESSION_HISTORY_MAX_PAGES);

  const truncated = Boolean(nextToken);
  log("fetchPodSessionHistory → complete", {
    totalItems: items.length,
    truncated,
  });

  return { items, truncated };
}

export async function fetchPodSessionClaimState(id) {
  log("fetchPodSessionClaimState → start", { id });

  const { data } = await API.graphql({
    query: GetPodSessionClaimState,
    variables: { id },
  });

  const claimState = data.getPodSession ?? null;
  log("fetchPodSessionClaimState → response", claimState);

  return claimState;
}

// A stored proctor can be unclaimed in more spellings than a DynamoDB condition
// can express (case variants, surrounding whitespace), so the caller pins the
// exact value it read and saw as unclaimed. Anyone claiming in between changes
// the value and the conditional write still fails.
export function buildUnclaimedProctorCondition(expectedProctor) {
  const values = [...CANONICAL_UNCLAIMED_PROCTOR_VALUES];

  if (
    typeof expectedProctor === "string" &&
    isUnclaimedProctor(expectedProctor) &&
    !values.includes(expectedProctor)
  ) {
    values.push(expectedProctor);
  }

  return {
    or: [
      { proctor: { attributeExists: false } },
      ...values.map((value) => ({ proctor: { eq: value } })),
    ],
  };
}

export async function claimPodSessionIfUnclaimed(
  id,
  proctor,
  { expectedProctor, authMode } = {},
) {
  log("claimPodSessionIfUnclaimed → start", {
    id,
    proctor,
    expectedProctor,
    authMode,
  });

  const resolvedAuthMode = authMode || "AMAZON_COGNITO_USER_POOLS";
  const input = { id, proctor };

  try {
    const { data } = await API.graphql({
      query: ClaimPodSession,
      variables: {
        input,
        condition: buildUnclaimedProctorCondition(expectedProctor),
      },
      authMode: resolvedAuthMode,
    });

    log("claimPodSessionIfUnclaimed → response", data);

    const updatedPodSession = data.updatePodSession;
    const actor = await fetchCurrentActor(resolvedAuthMode);

    const historyInput = {
      id: input.id,
      timestamp: new Date().toISOString(),
      actor,
      input: JSON.stringify(input),
      output: JSON.stringify(updatedPodSession),
    };

    log("createPodSessionHistory → start", historyInput);
    const historyError = await writePodSessionHistory(
      historyInput,
      resolvedAuthMode,
    );

    return attachHistoryError(updatedPodSession, historyError);
  } catch (error) {
    if (isConditionalCheckFailed(error)) {
      const session = getConditionalCheckFailedData(error);
      log("claimPodSessionIfUnclaimed → already claimed", session);
      throw new PodSessionAlreadyClaimedError(session);
    }

    throw error;
  }
}

export async function publishPodSessionClaim(sessionId) {
  log("publishPodSessionClaim → start", { sessionId });

  const { data } = await API.graphql({
    query: notifyPodSessionClaim,
    variables: { id: sessionId },
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  log("publishPodSessionClaim → response", data?.onClaim);
  return data?.onClaim ?? null;
}

export async function claimPodSessionAndNotify(id, proctor, options) {
  const session = await claimPodSessionIfUnclaimed(id, proctor, options);
  const claimedId = session?.id || id;

  try {
    await publishPodSessionClaim(claimedId);
    return {
      session,
      notificationError: null,
      historyError: session?.historyError ?? null,
    };
  } catch (error) {
    console.error("onClaim notification failed:", error);
    return {
      session,
      notificationError: error,
      historyError: session?.historyError ?? null,
    };
  }
}

export async function fetchOrganizationDetails(organizationId) {
  log("fetchOrganizationDetails → start", { organizationId });

  const { data } = await API.graphql({
    query: GetOrganizationDetails,
    variables: { organizationId },
  });

  const organization = JSON.parse(data.getOrganizationDetails).body;
  log("fetchOrganizationDetails → response", organization);

  return organization;
}

export async function fetchAllPodAgentAlerts(organizationId) {
  const organizationIds = getPodAgentAlertOrganizationIds(organizationId);
  log("fetchAllPodAgentAlerts → organization ids", organizationIds);

  const alertsByOrganization = await Promise.all(
    organizationIds.map((id) =>
      fetchAllPages(
        `fetchAllPodAgentAlerts:${id}`,
        PodAgentAlertsByOrganizationId,
        "podAgentAlertsByOrganizationId",
        { organizationId: id },
      ),
    ),
  );

  return alertsByOrganization.flat();
}

export async function fetchAllUsers() {
  return fetchAllPages("fetchAllUsers", listUsers, "listUsers", {});
}

export async function fetchOrCreateUser() {
  log("fetchOrCreateUser → Auth.currentUserInfo");

  const cognitoUser = await Auth.currentUserInfo();
  const email = cognitoUser.attributes.email;

  log("fetchOrCreateUser → Auth.currentUserInfo response", {
    id: cognitoUser.id,
    email,
  });

  log("getUser → start", { id: cognitoUser.id });

  const { data } = await API.graphql({
    query: getUser,
    variables: { id: cognitoUser.id },
  });

  log("getUser → response", data.getUser);

  if (data.getUser === null) {
    log("createUser → start", { id: cognitoUser.id, email });

    const { data: createData } = await API.graphql({
      query: createUser,
      variables: {
        input: {
          id: cognitoUser.id,
          email,
        },
      },
    });

    log("createUser → response", createData.createUser);

    return { user: createData.createUser, email };
  }

  return { user: data.getUser, email };
}

export async function fetchAllPodExamCodes(clientId) {
  const results = await fetchAllPages(
    "fetchAllPodExamCodes",
    ListPodExamCodesByClientId,
    "podExamCodesByClientId",
    { client_id: clientId },
  );

  log("fetchAllPodExamCodes → response", results);

  return results;
}

export async function fetchPodNotesFormat(organizationId) {
  log("fetchPodNotesFormat → start", { organizationId });

  const { data } = await API.graphql({
    query: GetPodNotesFormat,
    variables: { id: organizationId },
  });

  const format = data.getPodNotesFormat ?? null;
  log("fetchPodNotesFormat → response", format);

  return format;
}

export async function fetchCBRegistration(keycode) {
  const normalizedKeycode = String(keycode ?? "")
    .trim()
    .toLowerCase();
  if (!normalizedKeycode) return null;

  log("fetchCBRegistration → start", { keycode: normalizedKeycode });

  const { data } = await API.graphql({
    query: getCBRegistrations,
    variables: { keycode: normalizedKeycode },
  });

  const registration = data.getCBRegistrations ?? null;
  log("fetchCBRegistration → response", registration);

  return registration;
}

export async function updatePodSession(input, authMode) {
  log("updatePodSession → start", { input, authMode });

  const resolvedAuthMode = authMode || "AMAZON_COGNITO_USER_POOLS";

  const { data } = await API.graphql({
    query: UpdatePodSession,
    variables: { input },
    authMode: resolvedAuthMode,
  });

  log("updatePodSession → response", data);

  const updatedPodSession = data.updatePodSession;
  const actor = await fetchCurrentActor(resolvedAuthMode);

  const historyInput = {
    id: input.id,
    timestamp: new Date().toISOString(),
    actor,
    input: JSON.stringify(input),
    output: JSON.stringify(updatedPodSession),
  };

  log("createPodSessionHistory → start", historyInput);
  const historyError = await writePodSessionHistory(
    historyInput,
    resolvedAuthMode,
  );

  return attachHistoryError(updatedPodSession, historyError);
}

/**
 * Subscribe to PodSession updates filtered by id.
 * Returns an Amplify Zen Observable subscription (call .unsubscribe()).
 */
export function subscribeToPodSessionUpdates(sessionId, { next, error } = {}) {
  if (!sessionId) {
    return { unsubscribe: () => {} };
  }

  log("subscribeToPodSessionUpdates → start", { sessionId });

  return API.graphql({
    query: onUpdatePodSession,
    variables: {
      filter: {
        id: { eq: sessionId },
      },
    },
    authMode: "AMAZON_COGNITO_USER_POOLS",
  }).subscribe({
    next: ({ value }) => {
      const updated = value?.data?.onUpdatePodSession;
      if (!updated?.id || updated.id !== sessionId) return;
      log("subscribeToPodSessionUpdates → event", {
        id: updated.id,
        updatedAt: updated.updatedAt,
      });
      next?.(updated);
    },
    error: (err) => {
      console.warn(`${LOG_PREFIX} subscribeToPodSessionUpdates error:`, err);
      error?.(err);
    },
  });
}

export async function fetchCraneGuageSession(sessionId) {
  log("fetchCraneGuageSession → start", { sessionId });

  const { data } = await API.graphql({
    query: getCraneGuageSession,
    variables: { session_id: sessionId },
  });

  log("fetchCraneGuageSession → response", data.getCraneGuageSession);
  return data.getCraneGuageSession;
}

export async function listAllCraneGuageSessions() {
  log("listAllCraneGuageSessions → start");

  const items = await fetchAllCraneGuageSessionPages(async (variables) => {
    const { data } = await API.graphql({
      query: listCraneGuageSessions,
      variables,
    });
    return data.listCraneGuageSessions;
  });

  log("listAllCraneGuageSessions → response", { count: items.length });
  return items;
}

export async function updateCraneGuageSessionMeetLink(sessionId, meetingUri) {
  log("updateCraneGuageSessionMeetLink → start", { sessionId, meetingUri });

  const { data } = await API.graphql({
    query: UpdateCraneGuageSession,
    variables: {
      input: {
        session_id: sessionId,
        google_meet_id: meetingUri,
      },
    },
  });

  log(
    "updateCraneGuageSessionMeetLink → response",
    data.updateCraneGuageSession,
  );
  return data.updateCraneGuageSession;
}

export async function fetchGoogleRoomDetails(action, payload) {
  log("fetchGoogleRoomDetails → start", { action, payload });

  const { data } = await API.graphql({
    query: getGoogleRoomDetails,
    variables: {
      action,
      payload: JSON.stringify(payload),
    },
  });

  const parsed = JSON.parse(data.getGoogleRoomDetails);
  log("fetchGoogleRoomDetails → response", parsed);

  return parsed;
}

export async function syncPodMasterSheetToGoogle(podSession) {
  log("syncPodMasterSheetToGoogle → start", { id: podSession?.id });

  const { data } = await API.graphql({
    query: syncPodMasterSheet,
    variables: {
      podSession: JSON.stringify(podSession),
    },
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  const parsed = JSON.parse(data.syncPodMasterSheet);
  log("syncPodMasterSheetToGoogle → response", parsed);

  if (parsed.error) {
    throw new Error(parsed.error);
  }

  return parsed;
}

export async function fetchServerTime() {
  log("fetchServerTime → start");

  const { data } = await API.graphql({
    query: getServerTime,
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  const serverTime = data?.getServerTime;
  const serverTimeString = String(serverTime ?? "").trim();

  if (!serverTimeString) {
    throw new Error("Invalid server time response");
  }

  // Validate an absolute timestamp from the server (not local clock generation).
  if (!/([zZ]|[+-]\d{2}:?\d{2})$/.test(serverTimeString)) {
    throw new Error("Invalid server time response");
  }

  const parsedMs = Date.parse(serverTimeString);
  if (Number.isNaN(parsedMs)) {
    throw new Error("Invalid server time response");
  }

  log("fetchServerTime → response", serverTimeString);
  return serverTimeString;
}

export async function invokeSubmitPodProcessor(podSession) {
  log("invokeSubmitPodProcessor → start", { id: podSession?.id });

  const { data } = await API.graphql(
    {
      query: submitPodProcessor,
      variables: {
        PodSession: JSON.stringify(podSession),
      },
    },
    {
      authMode: "AMAZON_COGNITO_USER_POOLS",
    },
  );

  const result = data.submitPodProcessor;
  log("invokeSubmitPodProcessor → response", result);

  return result;
}

export async function updateUserGoogleTokens(userId, tokens) {
  log("updateUserGoogleTokens → start", { userId, tokens });

  const { data } = await API.graphql({
    query: updateUser,
    variables: {
      input: {
        id: userId,
        ...tokens,
      },
    },
  });

  log("updateUserGoogleTokens → response", data.updateUser);

  return data.updateUser;
}
